import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Loader2, Save, Send, Sparkles, Trash2 } from "lucide-react";
import { Card, Eyebrow, btnDark, btnLight, usDate, usDateTime } from "./ui";

const SECTIONS = [
  ["headline", "Headline"], ["cash_position", "Cash position"], ["burn_runway", "Cash burn & runway"],
  ["cash_flow_health", "Cash-flow health"], ["tax_opportunities", "Tax-saving opportunities"], ["debt_plan", "Debt reduction plan"],
];

export default function ReportTab() {
  const [reports, setReports] = useState([]);
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState("");
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");

  const load = async (pickId) => {
    const r = (await api.get("/advisory/reports")).data || [];
    setReports(r);
    const pick = r.find((x) => x.id === pickId) || r[0] || null;
    setSel(pick);
    setDraft(pick ? { ...pick.sections, action_items: (pick.sections.action_items || []).join("\n") } : null);
  };
  useEffect(() => {
    load();
    api.get("/users").then((r) => setClients((r.data || []).filter((u) => u.role === "client" && u.is_active))).catch(() => {});
  }, []);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (err) { toast.error(err?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(""); }
  };
  const generate = () => run("gen", async () => {
    const r = await api.post("/advisory/reports");
    toast.success("AI summary drafted — review and edit before sending");
    await load(r.data.id);
  });
  const save = () => run("save", async () => {
    const sections = { ...draft, action_items: draft.action_items.split("\n").map((s) => s.trim()).filter(Boolean) };
    await api.patch(`/advisory/reports/${sel.id}`, { sections });
    toast.success("Report saved");
    await load(sel.id);
  });
  const download = () => run("pdf", async () => {
    const r = await api.get(`/advisory/reports/${sel.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `financial_health_summary_${sel.created_at.slice(0, 10)}.pdf`; a.click();
    URL.revokeObjectURL(url);
  });
  const deliver = () => run("send", async () => {
    const sections = { ...draft, action_items: draft.action_items.split("\n").map((x) => x.trim()).filter(Boolean) };
    await api.patch(`/advisory/reports/${sel.id}`, { sections });
    await api.post(`/advisory/reports/${sel.id}/deliver`, { client_user_id: clientId });
    toast.success("Delivered to the client portal");
    await load(sel.id);
  });
  const remove = () => run("del", async () => {
    if (!window.confirm("Delete this report draft?")) return;
    await api.delete(`/advisory/reports/${sel.id}`);
    await load();
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
      <Card className="lg:col-span-3" testid="report-list-card">
        <button onClick={generate} disabled={!!busy} className={`${btnDark} w-full justify-center`} data-testid="report-generate-btn">
          {busy === "gen" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {busy === "gen" ? "Writing summary…" : "Generate AI summary"}
        </button>
        <div className="text-[11px] text-[var(--ll-ink-muted)] mt-2">Uses current cash, forecast, tax & debt figures. Takes ~20 seconds.</div>
        <ul className="mt-4 divide-y" style={{ borderColor: "var(--ll-border)" }}>
          {reports.map((r) => (
            <li key={r.id}>
              <button
                onClick={() => load(r.id)}
                className={`w-full text-left py-2.5 px-2 text-sm transition-colors ${sel?.id === r.id ? "bg-[var(--ll-mint)]" : "hover:bg-[var(--ll-mint)]"}`}
                data-testid={`report-item-${r.id}`}
              >
                <div className="font-medium">{usDate(r.created_at)}</div>
                <div className="text-[11px] text-[var(--ll-ink-soft)]">{r.created_by_name} · {r.deliveries?.length ? `sent ×${r.deliveries.length}` : "draft"}</div>
              </button>
            </li>
          ))}
          {reports.length === 0 && <li className="py-6 text-xs text-center text-[var(--ll-ink-muted)]" data-testid="report-empty">No summaries yet</li>}
        </ul>
      </Card>
      <div className="lg:col-span-9">
        {!sel || !draft ? (
          <Card testid="report-placeholder"><div className="text-sm text-[var(--ll-ink-soft)]">Generate a plain-English Financial Health Summary your client can read in two minutes.</div></Card>
        ) : (
          <Card testid="report-editor">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div><Eyebrow>Financial Health Summary</Eyebrow><div className="font-display font-bold text-lg">Review & edit before sending</div></div>
              <div className="flex flex-wrap gap-2">
                <button onClick={save} disabled={!!busy} className={btnDark} data-testid="report-save-btn">{busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
                <button onClick={download} disabled={!!busy} className={btnLight} style={{ borderColor: "var(--ll-border)" }} data-testid="report-pdf-btn">{busy === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} PDF</button>
                <button onClick={remove} disabled={!!busy} className={btnLight} style={{ borderColor: "var(--ll-border)" }} data-testid="report-delete-btn"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="space-y-4">
              {SECTIONS.map(([k, label]) => (
                <label key={k} className="block">
                  <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{label}</span>
                  <textarea
                    value={draft[k] || ""}
                    onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                    rows={k === "headline" ? 2 : 4}
                    className="mt-1 w-full border px-3 py-2 text-sm leading-relaxed"
                    style={{ borderColor: "var(--ll-border)" }}
                    data-testid={`report-section-${k}`}
                  />
                </label>
              ))}
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Top action items (one per line)</span>
                <textarea value={draft.action_items} onChange={(e) => setDraft({ ...draft, action_items: e.target.value })} rows={5}
                  className="mt-1 w-full border px-3 py-2 text-sm" style={{ borderColor: "var(--ll-border)" }} data-testid="report-section-action_items" />
              </label>
            </div>
            <div className="mt-5 pt-4 border-t flex flex-wrap items-center gap-2" style={{ borderColor: "var(--ll-border)" }}>
              <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="px-3 py-2 border bg-white text-sm min-w-[240px]" style={{ borderColor: "var(--ll-border)" }} data-testid="report-client-select">
                <option value="">{clients.length ? "Choose client…" : "No client users in this workspace"}</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
              </select>
              <button onClick={deliver} disabled={!clientId || !!busy} className={btnDark} data-testid="report-deliver-btn">
                {busy === "send" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Deliver to client portal
              </button>
              <span className="text-[11px] text-[var(--ll-ink-muted)]">Your edits are saved automatically before delivery.</span>
            </div>
            {sel.deliveries?.length > 0 && (
              <ul className="mt-3 text-xs text-[var(--ll-ink-soft)] space-y-1" data-testid="report-deliveries">
                {sel.deliveries.map((d) => <li key={d.shared_report_id}>Sent to {d.client_name} · {usDateTime(d.delivered_at)} by {d.delivered_by_name}</li>)}
              </ul>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
